import { buildTitle } from "@/lib/seo-quality";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { AlertTriangle, Mail, Phone, Snowflake, Wrench } from "lucide-react";
import { PageHeader } from "./fordon";
import { QuickPartContact } from "@/components/QuickPartContact";
import { RelatedLinks } from "@/components/RelatedLinks";
import { clampDescription } from "@/lib/seo-quality";
import { getPartsCategory, PARTS_CATEGORIES } from "@/data/reservdelar-kategorier";
import { PARTS_BRANDS } from "@/data/reservdelar-brands";

const SITE = "https://www.akhusbilfritid.se";

export const Route = createFileRoute("/reservdelar_/kategori/$slug")({
  codeSplitGroupings: [["loader", "component"]],
  loader: ({ params }) => {
    const cat = getPartsCategory(params.slug);
    if (!cat) throw notFound();
    return { cat };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const c = loaderData.cat;
    const url = `${SITE}/reservdelar/kategori/${c.slug}`;
    const title = buildTitle(`${c.name} – reservdelar husbil och husvagn`);
    const desc = clampDescription(
      `${c.name} till husbil och husvagn: ${c.parts
        .slice(0, 3)
        .map((p) => p.part.toLowerCase())
        .join(", ")}. Prisspann, felsökning och vad vi behöver veta – svar inom 24 h.`,
    );
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { name: "keywords", content: c.keywords },
        { property: "og:type", content: "website" },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Hem", item: `${SITE}/` },
                  { "@type": "ListItem", position: 2, name: "Reservdelar", item: `${SITE}/reservdelar` },
                  { "@type": "ListItem", position: 3, name: c.name, item: url },
                ],
              },
              {
                "@type": "FAQPage",
                "@id": `${url}#faq`,
                mainEntity: c.faq.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
              {
                "@type": "Service",
                "@id": `${url}#service`,
                name: `${c.name} till husbil och husvagn`,
                serviceType: c.name,
                url,
                areaServed: { "@type": "Country", name: "Sverige" },
                provider: {
                  "@type": "AutoPartsStore",
                  name: "AK Husbil & Fritid",
                  telephone: "+46708629743",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: "Stabsvägen 5",
                    postalCode: "952 51",
                    addressLocality: "Kalix",
                    addressCountry: "SE",
                  },
                },
              },
            ],
          }),
        },
      ],
    };
  },
  component: CategoryParts,
});

function CategoryParts() {
  const { cat: c } = Route.useLoaderData();
  const brands = PARTS_BRANDS.filter((b) => c.brandSlugs.includes(b.slug));

  return (
    <main className="pb-20">
      <PageHeader label="Reservdelar" title={c.name} sub={c.tagline} />

      <section className="container-edge">
        <div className="mx-auto max-w-3xl">
          <div className="rounded-3xl border border-accent/30 bg-accent/5 p-6">
            <p className="font-display text-lg">Kort svar</p>
            <ul className="mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground">
              <li>
                <strong className="text-foreground">Vanligaste delarna:</strong>{" "}
                {c.parts
                  .slice(0, 3)
                  .map((p) => `${p.part} (${p.price})`)
                  .join(", ")}
                .
              </li>
              <li>
                <strong className="text-foreground">Detta behöver vi:</strong> bild på delen,
                chassinummer och årsmodell — plus var delen sitter i skissen.
              </li>
              <li>
                <strong className="text-foreground">Svarstid:</strong> pris och leveranstid inom 24 h
                på vardagar; lagerdelar 1–3 arbetsdagar.
              </li>
              <li>
                <strong className="text-foreground">Montering:</strong> egen verkstad i Kalix, eller
                del skickad med monteringsanvisning.
              </li>
            </ul>
          </div>
          <QuickPartContact label={c.name} />

          <p className="mt-8 text-lg leading-relaxed text-muted-foreground">{c.intro}</p>


          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/reservdelar"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
            >
              <Mail className="h-4 w-4" /> Skicka delförfrågan
            </Link>
            <a
              href="tel:+46708629743"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium transition hover:bg-secondary"
            >
              <Phone className="h-4 w-4" /> Ring 070-862 97 43
            </a>
          </div>

          <h2 className="mt-14 font-display text-2xl md:text-3xl">
            Så identifierar vi rätt del
          </h2>
          <p className="mt-4 leading-relaxed text-muted-foreground">{c.identify}</p>
          <p className="mt-4 text-sm text-muted-foreground">
            I{" "}
            <Link to="/reservdelar" className="underline decoration-accent/60 underline-offset-4">
              delförfrågan
            </Link>{" "}
            får du peka ut på en skiss exakt var delen sitter — det är ofta det som avgör om vi kan
            beställa rätt del direkt.
          </p>

          <h2 className="mt-14 font-display text-2xl md:text-3xl">Delar och prisspann</h2>
          <div className="mt-6 overflow-x-auto rounded-2xl border border-border">
            <table className="w-full min-w-[520px] text-left text-sm">
              <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Del</th>
                  <th className="px-4 py-3">Detta behöver vi veta</th>
                  <th className="px-4 py-3 whitespace-nowrap">Prisspann</th>
                </tr>
              </thead>
              <tbody>
                {c.parts.map((p) => (
                  <tr key={p.part} className="border-t border-border align-top">
                    <td className="px-4 py-3 font-medium">{p.part}</td>
                    <td className="px-4 py-3 text-muted-foreground">{p.spec}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{p.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Priser är ungefärliga inklusive moms, exklusive montering, och varierar med årsmodell och
            leverantör.
          </p>

          <h2 className="mt-14 font-display text-2xl md:text-3xl">Felsökning: symptom och orsak</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {c.symptoms.map((s) => (
              <div key={s.symptom} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-center gap-2 text-accent">
                  <AlertTriangle className="h-4 w-4" />
                  <p className="text-sm font-medium text-foreground">{s.symptom}</p>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.cause}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-3xl border border-accent/30 bg-accent/5 p-6">
            <div className="flex items-center gap-2 text-accent">
              <Snowflake className="h-4 w-4" />
              <p className="font-display text-lg text-foreground">Norrbotten & vinterbruk</p>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{c.nordic}</p>
          </div>

          <h2 className="mt-14 font-display text-2xl md:text-3xl">Frågor & svar</h2>
          <div className="mt-6 space-y-4">
            {c.faq.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border bg-card p-5">
                <p className="font-medium">{f.q}</p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>

          {brands.length > 0 && (
            <div className="mt-14 rounded-3xl border border-border bg-secondary/40 p-6">
              <div className="flex items-center gap-2">
                <Wrench className="h-4 w-4 text-accent" />
                <p className="font-display text-lg">{c.name} per märke</p>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {brands.map((b) => (
                  <Link
                    key={b.slug}
                    to="/reservdelar/$brand"
                    params={{ brand: b.slug }}
                    className="rounded-full border border-border bg-background px-4 py-2 text-sm transition hover:bg-secondary"
                  >
                    {b.name}
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 rounded-3xl border border-border bg-card p-6">
            <p className="font-display text-lg">Andra reservdelskategorier</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {PARTS_CATEGORIES.filter((x) => x.slug !== c.slug).map((x) => (
                <Link
                  key={x.slug}
                  to="/reservdelar/kategori/$slug"
                  params={{ slug: x.slug }}
                  className="rounded-full border border-border bg-background px-4 py-2 text-sm transition hover:bg-secondary"
                >
                  {x.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-12">
            <RelatedLinks
              keyId={`reservdelar-kat-${c.slug}`}
              text={`${c.name} reservdel husbil husvagn verkstad service ${c.keywords}`}
              currentHref={`/reservdelar/kategori/${c.slug}`}
              max={6}
            />
          </div>
        </div>
      </section>
    </main>
  );
}
